import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { AlertTriangle, FlaskConical, Microscope, ThermometerSun, ArrowRight } from "lucide-react";
import { Reveal } from "../../lib/motion";
import { IMAGES } from "../../data";

const HARMS = [
  { icon: FlaskConical, title: "Chemical Leaching", desc: "Plastic food containers can leach BPA & phthalates into food — endocrine disruptors linked to hormonal and metabolic harm." },
  { icon: ThermometerSun, title: "Toxins When Heated", desc: "Microwaving or serving hot, oily food in plastic accelerates the release of harmful chemicals into every meal." },
  { icon: Microscope, title: "Microplastics In Us", desc: "Single-use plastics fragment into microplastics now detected in human blood, lungs and the food chain." },
  { icon: AlertTriangle, title: "Never Truly Gone", desc: "Only a fraction of plastic is ever recycled — the rest lingers for centuries, breaking apart but never breaking down." },
];

const STATS = [
  { value: "500+", label: "years for plastic to break down" },
  { value: "~9%", label: "of all plastic ever recycled" },
  { value: "400M+", label: "tonnes of plastic waste a year" },
  { value: "30–90", label: "days for Verraleaf to compost" },
];

export default function PlasticImpact({ onQuote }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <section ref={ref} data-testid="plastic-impact" className="relative bg-[#0a1f11] text-white overflow-hidden">
      <div className="absolute inset-0 grain-overlay pointer-events-none opacity-60" />

      {/* Problem intro */}
      <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <div className="lg:col-span-6">
            <Reveal>
              <span className="overline text-red-300/90">The Problem With Plastic</span>
              <h2 className="mt-6 font-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-white">
                Plastic wasn&apos;t built to disappear.
              </h2>
              <p className="mt-6 text-lg text-white/70 max-w-xl leading-relaxed">
                Conventional plastic and foam food containers are cheap for a reason — the true cost is paid by our health
                and the planet. What holds your meal for minutes can pollute the earth for centuries.
              </p>
            </Reveal>

            <div className="mt-10 grid sm:grid-cols-2 gap-4">
              {HARMS.map((h, i) => (
                <Reveal key={h.title} delay={0.06 * i}>
                  <div className="h-full rounded-2xl border border-white/10 bg-white/[0.04] p-6 hover:bg-white/[0.07] transition-colors duration-300">
                    <h.icon className="w-6 h-6 text-red-300 mb-3" />
                    <h3 className="font-display text-xl text-white">{h.title}</h3>
                    <p className="text-sm text-white/60 mt-2 leading-relaxed">{h.desc}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>

          <div className="lg:col-span-6">
            <Reveal delay={0.1}>
              <div className="relative rounded-[2rem] overflow-hidden border border-white/10">
                <img src={IMAGES.plasticWaste} alt="Discarded plastic food containers polluting a shoreline" loading="lazy"
                  className="w-full h-[300px] md:h-[420px] object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0a1f11]/70 to-transparent" />
                <p className="absolute bottom-5 left-5 right-5 text-sm text-white/80">
                  Plastic cups, lids and bottles washed up — the everyday afterlife of single-use food packaging.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </div>

      {/* Landfill impact band */}
      <div className="relative">
        <div className="relative h-[70vh] min-h-[460px] overflow-hidden">
          <motion.img style={{ y: imgY }} src={IMAGES.landfillAerial}
            alt="Aerial view of an overflowing landfill beside a city"
            className="absolute inset-0 w-full h-[116%] object-cover" loading="lazy" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0a1f11]/85 via-[#0a1f11]/55 to-[#0a1f11]/95" />
          <div className="relative h-full max-w-7xl mx-auto px-6 md:px-12 flex flex-col justify-center">
            <Reveal>
              <span className="overline text-verra-light">Filling The Earth</span>
              <h2 className="mt-5 font-display text-3xl md:text-5xl leading-tight max-w-3xl text-white">
                Every discarded container adds to a mountain that never shrinks.
              </h2>
              <p className="mt-5 max-w-xl text-white/75 leading-relaxed">
                Landfills swell with waste that won&apos;t decompose. As plastics fragment, microplastics seep into soil,
                groundwater and the crops we grow — a silent debt handed to the next generation.
              </p>
            </Reveal>
          </div>
        </div>

        <div className="relative bg-[#0a1f11]">
          <div className="max-w-7xl mx-auto px-6 md:px-12 -mt-16 relative z-10 grid grid-cols-2 lg:grid-cols-4 gap-4 pb-4">
            {STATS.map((s, i) => (
              <Reveal key={s.label} delay={0.06 * i}>
                <div className={`rounded-2xl p-6 h-full border ${i === STATS.length - 1 ? "bg-verra-green border-verra-green" : "bg-white/[0.05] border-white/10"}`}>
                  <p className="font-display text-4xl md:text-5xl text-white">{s.value}</p>
                  <p className={`text-sm mt-2 leading-snug ${i === STATS.length - 1 ? "text-white/90" : "text-white/60"}`}>{s.label}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>

      {/* Solution transition */}
      <div className="relative max-w-7xl mx-auto px-6 md:px-12 pb-24 md:pb-32 pt-14">
        <Reveal>
          <div className="rounded-[2rem] border border-verra-light/20 bg-verra-green/15 p-8 md:p-12 flex flex-col md:flex-row md:items-center gap-8 justify-between">
            <div className="max-w-2xl">
              <span className="overline text-verra-light">The Verraleaf Answer</span>
              <h3 className="mt-4 font-display text-3xl md:text-4xl text-white leading-tight">
                Made from sugarcane. Returns to the soil in weeks.
              </h3>
              <p className="mt-4 text-white/70 leading-relaxed">
                Verraleaf bagasse tableware is plastic-free, food-grade and fully compostable — the same convenience,
                without the centuries of harm. Choose packaging that gives back to the earth instead of burdening it.
              </p>
            </div>
            <button data-testid="plastic-quote-btn" onClick={onQuote}
              className="shrink-0 inline-flex items-center gap-2 rounded-full bg-white text-verra-blue font-semibold px-8 py-4 hover:bg-verra-light hover:text-verra-blue transition-colors duration-300">
              Switch to Bagasse <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
